import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { Form, Button, Card, Col, Row, Alert, Spinner } from "react-bootstrap";
import { FaServer } from "react-icons/fa";
import Title from "../common/Title";
import Utils from "../../utils";
import { runbookServerActions } from "../../actions/runbookServer.actions";
import { useToast } from "../../contexts/ToastContext";

function RunbookServerForm({ presetServerName, onSaveSuccess, onCancel, hideTitle }) {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { showToast } = useToast();
  const runbookServer = useSelector((state) => state.runbookServer);

  const queryParams = Utils.getQueryStringParams(location);
  const serverName = presetServerName ?? queryParams["serverName"];
  const isEditMode = !!serverName;

  const [formData, setFormData] = useState({
    serverName: "",
    description: "",
    serverUrl: "",
    serverKey: "",
  });

  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isEditMode) {
      dispatch(runbookServerActions.getById({ serverName: serverName }));
    }
    // eslint-disable-next-line
  }, [serverName]);

  useEffect(() => {
    if (isEditMode && runbookServer.server) {
      setFormData({
        serverName: runbookServer.server.serverName || "",
        description: runbookServer.server.description || "",
        serverUrl: runbookServer.server.serverUrl || "",
        serverKey: runbookServer.server.serverKey || "",
      });
    }
    // eslint-disable-next-line
  }, [runbookServer.server]);

  useEffect(() => {
    if (runbookServer.success && submitted) {
      showToast("Server saved successfully", "success");
      if (onSaveSuccess) {
        onSaveSuccess();
      } else {
        navigate("/product/servers");
      }
    }
    // eslint-disable-next-line
  }, [runbookServer.success]);

  const handleChange = (field) => (e) => {
    const value = e.target.value;
    setFormData({ ...formData, [field]: value });
    if (errors[field]) {
      setErrors({ ...errors, [field]: null });
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.serverName.trim()) {
      newErrors.serverName = "Server name is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);

    if (!validate()) {
      return;
    }

    if (isEditMode) {
      dispatch(runbookServerActions.update(formData));
    } else {
      dispatch(runbookServerActions.create(formData));
    }
  };

  return (
    <>
      {!hideTitle && (
        <Title
          text={isEditMode ? "Edit Runbook Server" : "Add Runbook Server"}
          icon={<FaServer className="mb-1" />}
        />
      )}

      {runbookServer.error && (
        <Alert variant="danger">
          An error occurred while saving the server. Please try again.
        </Alert>
      )}

      <Form onSubmit={handleSubmit}>
        <Card className="mb-3">
          <Card.Header>
            <Card.Title>Server Information</Card.Title>
          </Card.Header>
          <Card.Body>
            <Form.Group as={Row} className="mb-3">
              <Form.Label column sm={3}>
                Server Name <span className="text-danger">*</span>
              </Form.Label>
              <Col sm={9}>
                <Form.Control
                  type="text"
                  value={formData.serverName}
                  onChange={handleChange("serverName")}
                  disabled={isEditMode}
                  isInvalid={!!errors.serverName}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.serverName}
                </Form.Control.Feedback>
                {isEditMode && (
                  <Form.Text className="text-muted">
                    Server name cannot be changed after creation
                  </Form.Text>
                )}
              </Col>
            </Form.Group>

            <Form.Group as={Row} className="mb-3">
              <Form.Label column sm={3}>
                Description
              </Form.Label>
              <Col sm={9}>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={formData.description}
                  onChange={handleChange("description")}
                />
              </Col>
            </Form.Group>

            <Form.Group as={Row} className="mb-3">
              <Form.Label column sm={3}>
                Server URL
              </Form.Label>
              <Col sm={9}>
                <Form.Control
                  type="text"
                  value={formData.serverUrl}
                  onChange={handleChange("serverUrl")}
                  placeholder="https://server.example.com"
                />
              </Col>
            </Form.Group>

            <Form.Group as={Row} className="mb-3">
              <Form.Label column sm={3}>
                Server Key
              </Form.Label>
              <Col sm={9}>
                <Form.Control
                  type="text"
                  value={formData.serverKey}
                  onChange={handleChange("serverKey")}
                />
                <Form.Text className="text-muted">
                  Authentication key for the server
                </Form.Text>
              </Col>
            </Form.Group>
          </Card.Body>
        </Card>

        <div className="d-flex gap-2">
          <Button variant="primary" type="submit" disabled={runbookServer.loading}>
            {runbookServer.loading ? (
              <>
                <Spinner size="sm" className="me-2" />
                Saving...
              </>
            ) : (
              <>{isEditMode ? "Update" : "Create"} Server</>
            )}
          </Button>
          <Button
            variant="secondary"
            onClick={() => (onCancel ? onCancel() : navigate("/product/servers"))}
            disabled={runbookServer.loading}
          >
            Cancel
          </Button>
        </div>
      </Form>
    </>
  );
}

export default RunbookServerForm;
